import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Paper, Button, CircularProgress, Alert, TextField, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, List, ListItemButton, ListItemText, Divider, ListSubheader, ListItemIcon, Drawer, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import axiosInstance from '../services/axiosInstance';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BackupIcon from '@mui/icons-material/Backup';
import InventoryIcon from '@mui/icons-material/Inventory';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import BugReportIcon from '@mui/icons-material/BugReport';
import ImageIcon from '@mui/icons-material/Image';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import MenuIcon from '@mui/icons-material/Menu';
import CategoryIcon from '@mui/icons-material/Category';
import PaletteIcon from '@mui/icons-material/Palette';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StyleIcon from '@mui/icons-material/Style';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import BarChartIcon from '@mui/icons-material/BarChart';
import TerminalIcon from '@mui/icons-material/Terminal';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SyncIcon from '@mui/icons-material/Sync';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import RoleManagementPage from './RoleManagementPage';
import UserManagementPage from './UserManagementPage';
import ApiKeyManagement from '../components/ApiKeyManagement';
import BackupManagement from '../components/BackupManagement';
import UploadFavicon from '../components/admin/UploadFavicon';
import ShopCart from '../components/ShopCart';
import CsvImportExport from '../components/CsvImportExport';
import FieldDiagnostics from '../components/admin/FieldDiagnostics';
import ArchivePage from './ArchivePage';
import TrashPage from './TrashPage';
import DownloadTemplatesPage from './Settings/DownloadTemplatesPage';
import DuplicateListPage from '../features/duplicates/DuplicateListPage';
import CategoryManagementPage from './Settings/CategoryManagementPage';
import BadgeManagementPage from './Settings/BadgeManagementPage';
import BrandGuideManagementPage from './Settings/BrandGuideManagementPage';
import PptPoliciesManagementPage from './Settings/PptPoliciesManagementPage';
import LoginPageSettings from '../components/admin/LoginPageSettings';
import SupportPageSettings from '../components/admin/SupportPageSettings';
import AiQueueManagement from '../components/admin/AiQueueManagement';
import PopupSettingsModal from '../components/PopupSettingsModal';
import IssuesTasksPage from '../modules/issuesTasks/IssuesTasksPage';
import AnalyticsPage from './Settings/AnalyticsPage';
import ImageMagickTestPage from './Settings/ImageMagickTestPage';
import PdfDesignerPage from './Settings/PdfDesignerPage';
import DabasSyncPage from './Settings/DabasSyncPage';
import ProductComparisonPage from './Settings/ProductComparisonPage';
import ProductImageValidationPage from './Settings/ProductImageValidationPage';
import WordPressPublishingPage from './Settings/WordPressPublishingPage';
import DevSnapshotsPage from './Settings/DevSnapshotsPage';
import KnowledgeHubTestPage from '../modules/knowledgeHub/KnowledgeHubTestPage';
import MaintenanceSettingsPage from '../modules/maintenance/MaintenanceSettingsPage';
import PptxTemplateFieldImportPage from '../modules/pptxTemplateFieldImport/PptxTemplateFieldImportPage';
import { IUser, ICategory } from '../types/damTypes';
import { hasPermission, hasAnyPermission, canAccessProductComparison } from '../utils/permissionUtils';
import { APP_VERSION } from '../config/version';

const KNOWLEDGE_HUB_UI_ENABLED = process.env.REACT_APP_KNOWLEDGE_HUB_UI !== 'false';

type SettingsView =
  | 'roles'
  | 'users'
  | 'apikeys'
  | 'backups'
  | 'inventory'
  | 'csv'
  | 'pptx-template-field-import'
  | 'field-diagnostics'
  | 'favicon'
  | 'archive'
  | 'trash'
  | 'duplicates'
  | 'download-templates'
  | 'pdf-templates'
  | 'thumbnails'
  | 'categories'
  | 'badges'
  | 'support-page'
  | 'login-page'
  | 'ai-queue'
  | 'popup'
  | 'brand-guide'
  | 'ppt-policies'
  | 'analytics'
  | 'maintenance'
  | 'imagemagick-test'
  | 'issues-tasks'
  | 'dabas-sync'
  | 'product-comparison'
  | 'product-image-validation'
  | 'dev-snapshots'
  | 'knowledge-hub'
  | 'wordpress-publishing';

interface SettingsPageProps {
  currentView?: SettingsView;
  currentUser: IUser | null;
  onClose: () => void;
  brandGuideSlugToEdit?: string | null;
  onBrandGuideSlugHandled?: () => void;
}

const SETTINGS_SIDEBAR_WIDTH = 280;
const SETTINGS_SIDEBAR_COLLAPSED_WIDTH = 64;

const SettingsPage: React.FC<SettingsPageProps> = ({
  currentView = 'roles',
  currentUser,
  onClose,
  brandGuideSlugToEdit,
  onBrandGuideSlugHandled,
}) => {
  const [activeTab, setActiveTab] = useState<SettingsView>(currentView);
  const [warming, setWarming] = useState(false);
  const [warmDialogOpen, setWarmDialogOpen] = useState(false);
  const [warmResult, setWarmResult] = useState<{ generated:number; skipped:number; failed:number; totalTasks:number } | null>(null);
  const [warmProgress, setWarmProgress] = useState<{ generated:number; skipped:number; failed:number; totalTasks:number; done:boolean }>({ generated:0, skipped:0, failed:0, totalTasks:0, done:false });
  const [sinceHours, setSinceHours] = useState<number>(24);
  const [limit, setLimit] = useState<number>(0);
  const [scheduleEnabled, setScheduleEnabled] = useState<boolean>(false);
  const [scheduleTime, setScheduleTime] = useState<string>('02:00');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsSidebarCollapsed, setSettingsSidebarCollapsed] = useState(false);
  const [devSnapshotsEnabled, setDevSnapshotsEnabled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const settingsSidebarWidth = settingsSidebarCollapsed ? SETTINGS_SIDEBAR_COLLAPSED_WIDTH : SETTINGS_SIDEBAR_WIDTH;
  
  // Categories state for CategoryManagementPage
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  
  // Fetch categories when navigating to categories page
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      setCategoryError(null);
      const response = await axiosInstance.get<ICategory[]>(`/api/categories`);
      setCategories(response.data);
    } catch (err: any) {
      setCategoryError(err.response?.data?.message || err.message || 'Error fetching categories');
      console.error('Error fetching categories:', err.response?.data || err);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Badges state for BadgeManagementPage (needs categories)
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [badgeError, setBadgeError] = useState<string | null>(null);

  // Fetch categories when badges tab becomes active (badges need categories for visibility settings)
  const fetchCategoriesForBadges = async () => {
    try {
      setLoadingBadges(true);
      setBadgeError(null);
      const response = await axiosInstance.get<ICategory[]>(`/api/categories`);
      setCategories(response.data);
    } catch (err: any) {
      setBadgeError(err.response?.data?.message || err.message || 'Error fetching categories for badges');
      console.error('Error fetching categories for badges:', err.response?.data || err);
    } finally {
      setLoadingBadges(false);
    }
  };

  useEffect(() => {
    // Load schedule config
    (async () => {
      try {
        const { data } = await axiosInstance.get('/api/assets/previews/schedule');
        if (data?.config) {
          setScheduleEnabled(!!data.config.enabled);
          if (typeof data.config.dailyTime === 'string') setScheduleTime(data.config.dailyTime);
          if (Number(data.config.sinceHours) > 0) setSinceHours(Number(data.config.sinceHours));
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/api/dev-snapshots/status');
        setDevSnapshotsEnabled(Boolean(data?.enabled));
      } catch {
        setDevSnapshotsEnabled(false);
      }
    })();
  }, []);
  
  // Fetch categories when categories or badges tab becomes active
  useEffect(() => {
    if (activeTab === 'categories') {
      fetchCategories();
    }
    if (activeTab === 'badges') {
      fetchCategoriesForBadges();
    }
  }, [activeTab]);

  const allowedViews = useMemo<SettingsView[]>(() => [
    'roles',
    'users',
    'apikeys',
    'backups',
    'inventory',
    'csv',
    'pptx-template-field-import',
    'field-diagnostics',
    'favicon',
    'archive',
    'trash',
    'duplicates',
    'download-templates',
    'pdf-templates',
    'thumbnails',
    'categories',
    'badges',
    'support-page',
    'login-page',
    'ai-queue',
    'popup',
    'brand-guide',
    'ppt-policies',
    'analytics',
    'maintenance',
    'imagemagick-test',
    'issues-tasks',
    'dabas-sync',
    'product-comparison',
    'product-image-validation',
    'dev-snapshots',
    'knowledge-hub',
    'wordpress-publishing',
  ], []);

  const popupPermissions = useMemo(() => [
    'popup:read',
    'popup:create',
    'popup:update',
    'popup:delete',
    'popup:manageSettings',
  ], []);

  const canAccessView = useCallback((view: SettingsView): boolean => {
    switch (view) {
      case 'roles':
        return hasPermission(currentUser, 'roles:read');
      case 'users':
        return hasPermission(currentUser, 'users:read');
      case 'apikeys':
        return hasPermission(currentUser, 'apikeys:read');
      case 'backups':
        return hasPermission(currentUser, 'system:manageBackups');
      case 'inventory':
        return hasPermission(currentUser, 'system:manageInventory');
      case 'csv':
        return hasAnyPermission(currentUser, ['csv:import', 'csv:export', 'csv:manageTemplates']);
      case 'pptx-template-field-import':
        return hasPermission(currentUser, 'csv:import');
      case 'field-diagnostics':
        return hasPermission(currentUser, 'system:viewFieldDiagnostics');
      case 'favicon':
        return hasPermission(currentUser, 'system:manageBranding');
      case 'archive':
        return hasAnyPermission(currentUser, ['assets:archive', 'assets:update', 'assets:delete']);
      case 'trash':
        return hasAnyPermission(currentUser, ['admin:all', 'trash:read']);
      case 'duplicates':
        return hasPermission(currentUser, 'admin:all');
      case 'download-templates':
      case 'pdf-templates':
        return hasPermission(currentUser, 'downloadTemplates:read');
      case 'thumbnails':
        return hasPermission(currentUser, 'assets:update');
      case 'categories':
        return hasPermission(currentUser, 'categories:read');
      case 'badges':
        return hasPermission(currentUser, 'categories:create');
      case 'support-page':
        return hasPermission(currentUser, 'system:manageSettings');
      case 'login-page':
        return hasPermission(currentUser, 'system:manageLoginPage');
      case 'ai-queue':
        return hasPermission(currentUser, 'system:manageAiQueue');
      case 'popup':
        return hasAnyPermission(currentUser, popupPermissions);
      case 'brand-guide':
        return hasPermission(currentUser, 'brandGuide:update');
      case 'ppt-policies':
        return hasAnyPermission(currentUser, [
          'pptPolicies:update',
          'pptPolicies:publish',
          'pptPolicies:manageFiles',
        ]);
      case 'analytics':
        return hasPermission(currentUser, 'system:viewAnalytics');
      case 'maintenance':
        return hasPermission(currentUser, 'system:manageMaintenance');
      case 'imagemagick-test':
        return hasPermission(currentUser, 'system:manageImageTools');
      case 'issues-tasks':
        return hasAnyPermission(currentUser, ['issues:read', 'tasks:read']);
      case 'dabas-sync':
        return hasPermission(currentUser, 'system:manageDabasSync');
      case 'product-comparison':
        return canAccessProductComparison(currentUser);
      case 'product-image-validation':
        return hasPermission(currentUser, 'system:runProductImageValidation');
      case 'dev-snapshots':
        return devSnapshotsEnabled && hasAnyPermission(currentUser, ['admin:all', 'system:manageBackups']);
      case 'knowledge-hub':
        return KNOWLEDGE_HUB_UI_ENABLED && hasAnyPermission(currentUser, ['admin:all', 'knowledgeHub:read', 'knowledgeHub:admin']);
      case 'wordpress-publishing':
        return hasPermission(currentUser, 'assets:update');
      default:
        return false;
    }
  }, [currentUser, popupPermissions, devSnapshotsEnabled]);

  const firstAccessibleView = allowedViews.find(view => canAccessView(view));
  const showSystemSection =
    canAccessView('backups') ||
    canAccessView('inventory') ||
    canAccessView('analytics') ||
    canAccessView('maintenance') ||
    canAccessView('dabas-sync') ||
    canAccessView('product-comparison') ||
    canAccessView('product-image-validation') ||
    canAccessView('dev-snapshots') ||
    canAccessView('knowledge-hub') ||
    canAccessView('wordpress-publishing');
  const showToolsSection =
    canAccessView('issues-tasks') ||
    canAccessView('archive') ||
    canAccessView('trash') ||
    canAccessView('duplicates') ||
    canAccessView('field-diagnostics') ||
    canAccessView('favicon') ||
    canAccessView('login-page') ||
    canAccessView('download-templates') ||
    canAccessView('pdf-templates') ||
    canAccessView('popup') ||
    canAccessView('imagemagick-test');
  const showMediaSection = canAccessView('thumbnails') || canAccessView('ai-queue');

  const applyHash = useCallback(() => {
    try {
      const h = (window.location.hash || '').replace('#','');
      if (allowedViews.includes(h as SettingsView) && canAccessView(h as SettingsView)) {
        setActiveTab(h as SettingsView);
      } else if (firstAccessibleView) {
        setActiveTab(firstAccessibleView);
      }
    } catch {}
  }, [allowedViews, canAccessView, firstAccessibleView]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    const nextView = newValue as SettingsView;
    if (!canAccessView(nextView)) return;
    setActiveTab(nextView);
    try { window.location.hash = newValue; } catch {}
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleCollapsedSidebarTooltip = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const button = target.closest('.MuiListItemButton-root') as HTMLElement | null;
    if (!button) return;
    if (!settingsSidebarCollapsed) {
      button.removeAttribute('title');
      return;
    }
    const label = button.querySelector('.MuiListItemText-root')?.textContent?.trim();
    if (label) button.setAttribute('title', label);
  }, [settingsSidebarCollapsed]);

  useEffect(() => {
    // On mount: read hash and listen to changes
    applyHash();
    const onHash = () => applyHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [applyHash]);

  useEffect(() => {
    if (!firstAccessibleView) return;
    if (!canAccessView(activeTab)) {
      setActiveTab(firstAccessibleView);
    }
  }, [activeTab, firstAccessibleView, canAccessView]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && (
            <IconButton onClick={() => setMobileMenuOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h5">Systeminställningar</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeTab === 'thumbnails' && canAccessView('thumbnails') && !isMobile && (
            <Button
              size="small"
              variant="outlined"
              disabled={warming}
              onClick={async () => {
                try {
                  setWarming(true); setWarmDialogOpen(true); setWarmResult(null);
                  setWarmProgress({ generated:0, skipped:0, failed:0, totalTasks:0, done:false });
                  const body = { types: ['thumbnail','medium'], sizes: { thumbnail: [140,190,260], medium: [800] }, format: 'webp', sinceHours: sinceHours > 0 ? sinceHours : undefined, limit: limit > 0 ? limit : undefined, async: true };
                  const { data } = await axiosInstance.post('/api/assets/previews/warm', body);
                  const jobId = data.jobId as string;
                  setWarmProgress(prev => ({ ...prev, totalTasks: data.totalTasks || 0 }));
                  // start polling
                  const h = setInterval(async () => {
                    try {
                      const st = await axiosInstance.get('/api/assets/previews/warm/status', { params: { jobId } });
                      const job = st.data?.job;
                      if (job) {
                        setWarmProgress({ generated: job.generated||0, skipped: job.skipped||0, failed: job.failed||0, totalTasks: job.totalTasks||0, done: !!job.done });
                        if (job.done) {
                          clearInterval(h);
                          setWarmResult({ generated: job.generated||0, skipped: job.skipped||0, failed: job.failed||0, totalTasks: job.totalTasks||0 });
                          setWarming(false);
                        }
                      }
                    } catch {}
                  }, 1000);
                } catch (e:any) {
                  setWarmResult({ generated: 0, skipped: 0, failed: 1, totalTasks: 0 } as any);
                  setWarming(false);
                } finally { }
              }}
            >
              {warming ? (<><CircularProgress size={16} sx={{ mr: 1 }} />Värmer…</>) : 'Värm thumbnails'}
            </Button>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
        {/* Mobile Drawer */}
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 280, backgroundColor: '#fafafa' }
          }}
        >
          <List component="nav" aria-label="Settings menu" sx={{ py: 1 }}>
            {/* Administration */}
            <ListSubheader sx={{ backgroundColor: 'transparent', fontWeight: 600, color: '#072f53', lineHeight: '32px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'static' }}>
              Administration
            </ListSubheader>
            {hasPermission(currentUser, 'categories:read') && (
              <ListItemButton 
                selected={activeTab==='categories'} 
                onClick={(e)=>handleTabChange(e as any,'categories')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='categories' ? 'white' : '#072f53' }}>
                  <CategoryIcon />
                </ListItemIcon>
                <ListItemText primary="Kategorier" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'categories:create') && (
              <ListItemButton 
                selected={activeTab==='badges'} 
                onClick={(e)=>handleTabChange(e as any,'badges')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='badges' ? 'white' : '#072f53' }}>
                  <PaletteIcon />
                </ListItemIcon>
                <ListItemText primary="Märken" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'brandGuide:update') && (
              <ListItemButton
                selected={activeTab==='brand-guide'}
                onClick={(e)=>handleTabChange(e as any,'brand-guide')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='brand-guide' ? 'white' : '#072f53' }}>
                  <StyleIcon />
                </ListItemIcon>
                <ListItemText primary="Grafisk manual" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {canAccessView('ppt-policies') && (
              <ListItemButton
                selected={activeTab==='ppt-policies'}
                onClick={(e)=>handleTabChange(e as any,'ppt-policies')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='ppt-policies' ? 'white' : '#072f53' }}>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary="PPT Policys" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'users:read') && (
              <ListItemButton 
                selected={activeTab==='users'} 
                onClick={(e)=>handleTabChange(e as any,'users')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='users' ? 'white' : '#072f53' }}>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText primary="Användare" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'roles:read') && (
              <ListItemButton 
                selected={activeTab==='roles'} 
                onClick={(e)=>handleTabChange(e as any,'roles')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='roles' ? 'white' : '#072f53' }}>
                  <SecurityIcon />
                </ListItemIcon>
                <ListItemText primary="Roller" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'apikeys:read') && (
              <ListItemButton 
                selected={activeTab==='apikeys'} 
                onClick={(e)=>handleTabChange(e as any,'apikeys')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='apikeys' ? 'white' : '#072f53' }}>
                  <VpnKeyIcon />
                </ListItemIcon>
                <ListItemText primary="API-nycklar" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}

            {/* System */}
            {showSystemSection && (
              <>
                <Divider sx={{ my: 2, mx: 2 }} />
                <ListSubheader sx={{ backgroundColor: 'transparent', fontWeight: 600, color: '#072f53', lineHeight: '32px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'static' }}>
                  System
                </ListSubheader>
                {canAccessView('backups') && (
                <ListItemButton 
                  selected={activeTab==='backups'} 
                  onClick={(e)=>handleTabChange(e as any,'backups')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='backups' ? 'white' : '#072f53' }}>
                    <BackupIcon />
                  </ListItemIcon>
                  <ListItemText primary="Backuper" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('inventory') && (
                <ListItemButton 
                  selected={activeTab==='inventory'} 
                  onClick={(e)=>handleTabChange(e as any,'inventory')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='inventory' ? 'white' : '#072f53' }}>
                    <InventoryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Lager & Order" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('analytics') && (
                <ListItemButton 
                  selected={activeTab==='analytics'} 
                  onClick={(e)=>handleTabChange(e as any,'analytics')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='analytics' ? 'white' : '#072f53' }}>
                    <BarChartIcon />
                  </ListItemIcon>
                  <ListItemText primary="Analytics" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('maintenance') && (
                <ListItemButton
                  selected={activeTab==='maintenance'}
                  onClick={(e)=>handleTabChange(e as any,'maintenance')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='maintenance' ? 'white' : '#072f53' }}>
                    <AnnouncementIcon />
                  </ListItemIcon>
                  <ListItemText primary="Underhåll" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('dabas-sync') && (
                <ListItemButton 
                  selected={activeTab==='dabas-sync'} 
                  onClick={(e)=>handleTabChange(e as any,'dabas-sync')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='dabas-sync' ? 'white' : '#072f53' }}>
                    <SyncIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dabas-synk" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('product-comparison') && (
                <ListItemButton 
                  selected={activeTab==='product-comparison'} 
                  onClick={(e)=>handleTabChange(e as any,'product-comparison')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='product-comparison' ? 'white' : '#072f53' }}>
                    <CompareArrowsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Produktkontroll" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('product-image-validation') && (
                <ListItemButton 
                  selected={activeTab==='product-image-validation'} 
                  onClick={(e)=>handleTabChange(e as any,'product-image-validation')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='product-image-validation' ? 'white' : '#072f53' }}>
                    <FactCheckIcon />
                  </ListItemIcon>
                  <ListItemText primary="Bildkontroll Validoo" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('knowledge-hub') && (
                <ListItemButton
                  selected={activeTab==='knowledge-hub'}
                  onClick={(e)=>handleTabChange(e as any,'knowledge-hub')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='knowledge-hub' ? 'white' : '#072f53' }}>
                    <SmartToyIcon />
                  </ListItemIcon>
                  <ListItemText primary="Chatta med Athena" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('wordpress-publishing') && (
                <ListItemButton 
                  selected={activeTab==='wordpress-publishing'} 
                  onClick={(e)=>handleTabChange(e as any,'wordpress-publishing')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='wordpress-publishing' ? 'white' : '#072f53' }}>
                    <LanguageIcon />
                  </ListItemIcon>
                  <ListItemText primary="WordPress Publishing" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
              </>
            )}
            {hasAnyPermission(currentUser, ['csv:import', 'csv:export', 'csv:manageTemplates']) && (
              <ListItemButton 
                selected={activeTab==='csv'} 
                onClick={(e)=>handleTabChange(e as any,'csv')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='csv' ? 'white' : '#072f53' }}>
                  <ImportExportIcon />
                </ListItemIcon>
                <ListItemText primary="CSV Import/Export" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'csv:import') && (
              <ListItemButton
                selected={activeTab==='pptx-template-field-import'}
                onClick={(e)=>handleTabChange(e as any,'pptx-template-field-import')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='pptx-template-field-import' ? 'white' : '#072f53' }}>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary="PowerPoint-import" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}

            {/* Verktyg */}
            {showToolsSection && (
              <>
            <Divider sx={{ my: 2, mx: 2 }} />
            <ListSubheader sx={{ backgroundColor: 'transparent', fontWeight: 600, color: '#072f53', lineHeight: '32px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'static' }}>
              Verktyg
            </ListSubheader>
            {hasAnyPermission(currentUser, ['issues:read', 'tasks:read']) && (
              <ListItemButton
                selected={activeTab==='issues-tasks'}
                onClick={(e)=>handleTabChange(e as any,'issues-tasks')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='issues-tasks' ? 'white' : '#072f53' }}>
                  <BugReportIcon />
                </ListItemIcon>
                <ListItemText primary="Buggar & Roadmap" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {canAccessView('archive') && (
            <ListItemButton 
              selected={activeTab==='archive'} 
              onClick={(e)=>handleTabChange(e as any,'archive')}
              sx={{
                mx: 1,
                mb: 0.5,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: '#072f53',
                  color: 'white',
                  '&:hover': { backgroundColor: '#0a3d6b' },
                  '& .MuiListItemIcon-root': { color: 'white' }
                },
                '&:hover': {
                  backgroundColor: '#e3f2fd'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: activeTab==='archive' ? 'white' : '#072f53' }}>
                <ArchiveIcon />
              </ListItemIcon>
              <ListItemText primary="Arkiv" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </ListItemButton>
            )}
            {canAccessView('trash') && (
              <ListItemButton 
                selected={activeTab==='trash'} 
                onClick={(e)=>handleTabChange(e as any,'trash')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='trash' ? 'white' : '#072f53' }}>
                  <DeleteForeverIcon />
                </ListItemIcon>
                <ListItemText primary="Soptunna" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {(canAccessView('duplicates') || canAccessView('field-diagnostics') || canAccessView('favicon') || canAccessView('login-page') || canAccessView('support-page')) && (
              <>
                {canAccessView('duplicates') && (
                <ListItemButton 
                  selected={activeTab==='duplicates'} 
                  onClick={(e)=>handleTabChange(e as any,'duplicates')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='duplicates' ? 'white' : '#072f53' }}>
                    <ContentCopyIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dublettkontroll" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('field-diagnostics') && (
                <ListItemButton 
                  selected={activeTab==='field-diagnostics'} 
                  onClick={(e)=>handleTabChange(e as any,'field-diagnostics')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='field-diagnostics' ? 'white' : '#072f53' }}>
                    <BugReportIcon />
                  </ListItemIcon>
                  <ListItemText primary="Fältdiagnostik" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('favicon') && (
                <ListItemButton 
                  selected={activeTab==='favicon'} 
                  onClick={(e)=>handleTabChange(e as any,'favicon')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='favicon' ? 'white' : '#072f53' }}>
                    <ImageIcon />
                  </ListItemIcon>
                  <ListItemText primary="Favicon" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('login-page') && (
                <ListItemButton 
                  selected={activeTab==='login-page'} 
                  onClick={(e)=>handleTabChange(e as any,'login-page')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='login-page' ? 'white' : '#072f53' }}>
                    <PaletteIcon />
                  </ListItemIcon>
                  <ListItemText primary="Inloggningssida" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('support-page') && (
                <ListItemButton
                  selected={activeTab==='support-page'}
                  onClick={(e)=>handleTabChange(e as any,'support-page')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='support-page' ? 'white' : '#072f53' }}>
                    <HelpOutlineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Supportsida" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
              </>
            )}
            {hasPermission(currentUser, 'downloadTemplates:read') && (
              <ListItemButton 
                selected={activeTab==='download-templates'} 
                onClick={(e)=>handleTabChange(e as any,'download-templates')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='download-templates' ? 'white' : '#072f53' }}>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary="Nedladdningsmall Bilder" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'downloadTemplates:read') && (
              <ListItemButton 
                selected={activeTab==='pdf-templates'} 
                onClick={(e)=>handleTabChange(e as any,'pdf-templates')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='pdf-templates' ? 'white' : '#072f53' }}>
                  <PictureAsPdfIcon />
                </ListItemIcon>
                <ListItemText primary="PDF-mallar" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {canAccessView('popup') && (
              <ListItemButton 
                selected={activeTab==='popup'} 
                onClick={(e)=>handleTabChange(e as any,'popup')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='popup' ? 'white' : '#072f53' }}>
                  <AnnouncementIcon />
                </ListItemIcon>
                <ListItemText primary="Popup-nyheter" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {canAccessView('imagemagick-test') && (
              <ListItemButton 
                selected={activeTab==='imagemagick-test'} 
                onClick={(e)=>handleTabChange(e as any,'imagemagick-test')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='imagemagick-test' ? 'white' : '#072f53' }}>
                  <TerminalIcon />
                </ListItemIcon>
                <ListItemText primary="ImageMagick Test" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
              </>
            )}

            {/* Media */}
            {showMediaSection && (
              <>
                <Divider sx={{ my: 2, mx: 2 }} />
                <ListSubheader sx={{ backgroundColor: 'transparent', fontWeight: 600, color: '#072f53', lineHeight: '32px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'static' }}>
                  Media
                </ListSubheader>
                {canAccessView('thumbnails') && (
                <ListItemButton 
                  selected={activeTab==='thumbnails'} 
                  onClick={(e)=>handleTabChange(e as any,'thumbnails')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='thumbnails' ? 'white' : '#072f53' }}>
                    <PhotoLibraryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Thumbnails" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('ai-queue') && (
                <ListItemButton 
                  selected={activeTab==='ai-queue'} 
                  onClick={(e)=>handleTabChange(e as any,'ai-queue')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='ai-queue' ? 'white' : '#072f53' }}>
                    <SmartToyIcon />
                  </ListItemIcon>
                  <ListItemText primary="AI-kö" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
              </>
            )}
          </List>
          
          {/* Version number */}
          <Box sx={{ p: 2, mt: 'auto', borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" sx={{ color: '#999', display: 'block', textAlign: 'center' }}>
              Version {APP_VERSION}
            </Typography>
          </Box>
        </Drawer>

        {/* Desktop Sidebar */}
        <Box sx={{ 
          width: settingsSidebarWidth,
          flex: `0 0 ${settingsSidebarWidth}px`,
          flexShrink: 0,
          borderRight: '1px solid #e0e0e0', 
          backgroundColor: '#fafafa',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          transition: 'width 0.2s ease, flex-basis 0.2s ease',
          '& .MuiListSubheader-root': {
            display: settingsSidebarCollapsed ? 'none' : 'block',
          },
          '& .MuiListItemButton-root': {
            justifyContent: settingsSidebarCollapsed ? 'center' : 'flex-start',
            px: settingsSidebarCollapsed ? 0 : undefined,
          },
          '& .MuiListItemIcon-root': {
            minWidth: settingsSidebarCollapsed ? 0 : 40,
            justifyContent: 'center',
          },
          '& .MuiListItemText-root': {
            display: settingsSidebarCollapsed ? 'none' : 'block',
          },
          '& .MuiDivider-root': {
            mx: settingsSidebarCollapsed ? 1 : 2,
          },
        }}>
          <Box sx={{ display: 'flex', justifyContent: settingsSidebarCollapsed ? 'center' : 'flex-end', px: 1, pt: 1, pb: 0.5 }}>
            <Tooltip title={settingsSidebarCollapsed ? 'Visa menytext' : 'Dölj menytext'}>
              <IconButton
                size="small"
                onClick={() => setSettingsSidebarCollapsed((prev) => !prev)}
                aria-label={settingsSidebarCollapsed ? 'Visa settings-meny' : 'Dölj settings-meny'}
                sx={{ color: '#072f53' }}
              >
                {settingsSidebarCollapsed ? <KeyboardDoubleArrowRightIcon fontSize="small" /> : <KeyboardDoubleArrowLeftIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          <List component="nav" aria-label="Settings menu" onMouseOver={handleCollapsedSidebarTooltip} sx={{ py: 1 }}>
            {/* Administration */}
            <ListSubheader sx={{ backgroundColor: 'transparent', fontWeight: 600, color: '#072f53', lineHeight: '32px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'static' }}>
              Administration
            </ListSubheader>
            {hasPermission(currentUser, 'categories:read') && (
              <ListItemButton 
                selected={activeTab==='categories'} 
                onClick={(e)=>handleTabChange(e as any,'categories')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='categories' ? 'white' : '#072f53' }}>
                  <CategoryIcon />
                </ListItemIcon>
                <ListItemText primary="Kategorier" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'categories:create') && (
              <ListItemButton 
                selected={activeTab==='badges'} 
                onClick={(e)=>handleTabChange(e as any,'badges')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='badges' ? 'white' : '#072f53' }}>
                  <PaletteIcon />
                </ListItemIcon>
                <ListItemText primary="Märken" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'brandGuide:update') && (
              <ListItemButton
                selected={activeTab==='brand-guide'}
                onClick={(e)=>handleTabChange(e as any,'brand-guide')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='brand-guide' ? 'white' : '#072f53' }}>
                  <StyleIcon />
                </ListItemIcon>
                <ListItemText primary="Grafisk manual" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {canAccessView('ppt-policies') && (
              <ListItemButton
                selected={activeTab==='ppt-policies'}
                onClick={(e)=>handleTabChange(e as any,'ppt-policies')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='ppt-policies' ? 'white' : '#072f53' }}>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary="PPT Policys" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'users:read') && (
              <ListItemButton 
                selected={activeTab==='users'} 
                onClick={(e)=>handleTabChange(e as any,'users')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='users' ? 'white' : '#072f53' }}>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText primary="Användare" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'roles:read') && (
              <ListItemButton 
                selected={activeTab==='roles'} 
                onClick={(e)=>handleTabChange(e as any,'roles')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='roles' ? 'white' : '#072f53' }}>
                  <SecurityIcon />
                </ListItemIcon>
                <ListItemText primary="Roller" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'apikeys:read') && (
              <ListItemButton 
                selected={activeTab==='apikeys'} 
                onClick={(e)=>handleTabChange(e as any,'apikeys')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='apikeys' ? 'white' : '#072f53' }}>
                  <VpnKeyIcon />
                </ListItemIcon>
                <ListItemText primary="API-nycklar" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}

            {/* System */}
            {showSystemSection && (
              <>
                <Divider sx={{ my: 2, mx: 2 }} />
                <ListSubheader sx={{ backgroundColor: 'transparent', fontWeight: 600, color: '#072f53', lineHeight: '32px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'static' }}>
                  System
                </ListSubheader>
                {canAccessView('backups') && (
                <ListItemButton 
                  selected={activeTab==='backups'} 
                  onClick={(e)=>handleTabChange(e as any,'backups')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='backups' ? 'white' : '#072f53' }}>
                    <BackupIcon />
                  </ListItemIcon>
                  <ListItemText primary="Backuper" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('inventory') && (
                <ListItemButton 
                  selected={activeTab==='inventory'} 
                  onClick={(e)=>handleTabChange(e as any,'inventory')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='inventory' ? 'white' : '#072f53' }}>
                    <InventoryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Lager & Order" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('analytics') && (
                <ListItemButton 
                  selected={activeTab==='analytics'} 
                  onClick={(e)=>handleTabChange(e as any,'analytics')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='analytics' ? 'white' : '#072f53' }}>
                    <BarChartIcon />
                  </ListItemIcon>
                  <ListItemText primary="Analytics" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('maintenance') && (
                <ListItemButton
                  selected={activeTab==='maintenance'}
                  onClick={(e)=>handleTabChange(e as any,'maintenance')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='maintenance' ? 'white' : '#072f53' }}>
                    <AnnouncementIcon />
                  </ListItemIcon>
                  <ListItemText primary="Underhåll" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('dabas-sync') && (
                <ListItemButton 
                  selected={activeTab==='dabas-sync'} 
                  onClick={(e)=>handleTabChange(e as any,'dabas-sync')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='dabas-sync' ? 'white' : '#072f53' }}>
                    <SyncIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dabas-synk" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('product-comparison') && (
                <ListItemButton 
                  selected={activeTab==='product-comparison'} 
                  onClick={(e)=>handleTabChange(e as any,'product-comparison')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='product-comparison' ? 'white' : '#072f53' }}>
                    <CompareArrowsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Produktkontroll" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('product-image-validation') && (
                <ListItemButton 
                  selected={activeTab==='product-image-validation'} 
                  onClick={(e)=>handleTabChange(e as any,'product-image-validation')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='product-image-validation' ? 'white' : '#072f53' }}>
                    <FactCheckIcon />
                  </ListItemIcon>
                  <ListItemText primary="Bildkontroll Validoo" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('dev-snapshots') && (
                <ListItemButton
                  selected={activeTab==='dev-snapshots'}
                  onClick={(e)=>handleTabChange(e as any,'dev-snapshots')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='dev-snapshots' ? 'white' : '#072f53' }}>
                    <TerminalIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dev Snapshots" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('knowledge-hub') && (
                <ListItemButton
                  selected={activeTab==='knowledge-hub'}
                  onClick={(e)=>handleTabChange(e as any,'knowledge-hub')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='knowledge-hub' ? 'white' : '#072f53' }}>
                    <SmartToyIcon />
                  </ListItemIcon>
                  <ListItemText primary="Chatta med Athena" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('wordpress-publishing') && (
                <ListItemButton 
                  selected={activeTab==='wordpress-publishing'} 
                  onClick={(e)=>handleTabChange(e as any,'wordpress-publishing')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='wordpress-publishing' ? 'white' : '#072f53' }}>
                    <LanguageIcon />
                  </ListItemIcon>
                  <ListItemText primary="WordPress Publishing" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
              </>
            )}
            {hasAnyPermission(currentUser, ['csv:import', 'csv:export', 'csv:manageTemplates']) && (
              <ListItemButton 
                selected={activeTab==='csv'} 
                onClick={(e)=>handleTabChange(e as any,'csv')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='csv' ? 'white' : '#072f53' }}>
                  <ImportExportIcon />
                </ListItemIcon>
                <ListItemText primary="CSV Import/Export" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'csv:import') && (
              <ListItemButton
                selected={activeTab==='pptx-template-field-import'}
                onClick={(e)=>handleTabChange(e as any,'pptx-template-field-import')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='pptx-template-field-import' ? 'white' : '#072f53' }}>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary="PowerPoint-import" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}

            {/* Verktyg */}
            {showToolsSection && (
              <>
            <Divider sx={{ my: 2, mx: 2 }} />
            <ListSubheader sx={{ backgroundColor: 'transparent', fontWeight: 600, color: '#072f53', lineHeight: '32px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'static' }}>
              Verktyg
            </ListSubheader>
            {hasAnyPermission(currentUser, ['issues:read', 'tasks:read']) && (
              <ListItemButton
                selected={activeTab==='issues-tasks'}
                onClick={(e)=>handleTabChange(e as any,'issues-tasks')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='issues-tasks' ? 'white' : '#072f53' }}>
                  <BugReportIcon />
                </ListItemIcon>
                <ListItemText primary="Buggar & Roadmap" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {canAccessView('archive') && (
            <ListItemButton 
              selected={activeTab==='archive'} 
              onClick={(e)=>handleTabChange(e as any,'archive')}
              sx={{
                mx: 1,
                mb: 0.5,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: '#072f53',
                  color: 'white',
                  '&:hover': { backgroundColor: '#0a3d6b' },
                  '& .MuiListItemIcon-root': { color: 'white' }
                },
                '&:hover': {
                  backgroundColor: '#e3f2fd'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: activeTab==='archive' ? 'white' : '#072f53' }}>
                <ArchiveIcon />
              </ListItemIcon>
              <ListItemText primary="Arkiv" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </ListItemButton>
            )}
            {canAccessView('trash') && (
              <ListItemButton 
                selected={activeTab==='trash'} 
                onClick={(e)=>handleTabChange(e as any,'trash')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='trash' ? 'white' : '#072f53' }}>
                  <DeleteForeverIcon />
                </ListItemIcon>
                <ListItemText primary="Soptunna" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {(canAccessView('duplicates') || canAccessView('field-diagnostics') || canAccessView('favicon') || canAccessView('login-page') || canAccessView('support-page')) && (
              <>
                {canAccessView('duplicates') && (
                <ListItemButton 
                  selected={activeTab==='duplicates'} 
                  onClick={(e)=>handleTabChange(e as any,'duplicates')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='duplicates' ? 'white' : '#072f53' }}>
                    <ContentCopyIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dublettkontroll" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('field-diagnostics') && (
                <ListItemButton 
                  selected={activeTab==='field-diagnostics'} 
                  onClick={(e)=>handleTabChange(e as any,'field-diagnostics')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='field-diagnostics' ? 'white' : '#072f53' }}>
                    <BugReportIcon />
                  </ListItemIcon>
                  <ListItemText primary="Fältdiagnostik" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('favicon') && (
                <ListItemButton 
                  selected={activeTab==='favicon'} 
                  onClick={(e)=>handleTabChange(e as any,'favicon')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='favicon' ? 'white' : '#072f53' }}>
                    <ImageIcon />
                  </ListItemIcon>
                  <ListItemText primary="Favicon" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('login-page') && (
                <ListItemButton 
                  selected={activeTab==='login-page'} 
                  onClick={(e)=>handleTabChange(e as any,'login-page')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='login-page' ? 'white' : '#072f53' }}>
                    <PaletteIcon />
                  </ListItemIcon>
                  <ListItemText primary="Inloggningssida" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('support-page') && (
                <ListItemButton
                  selected={activeTab==='support-page'}
                  onClick={(e)=>handleTabChange(e as any,'support-page')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='support-page' ? 'white' : '#072f53' }}>
                    <HelpOutlineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Supportsida" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
              </>
            )}
            {hasPermission(currentUser, 'downloadTemplates:read') && (
              <ListItemButton 
                selected={activeTab==='download-templates'} 
                onClick={(e)=>handleTabChange(e as any,'download-templates')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='download-templates' ? 'white' : '#072f53' }}>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary="Nedladdningsmall Bilder" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {hasPermission(currentUser, 'downloadTemplates:read') && (
              <ListItemButton 
                selected={activeTab==='pdf-templates'} 
                onClick={(e)=>handleTabChange(e as any,'pdf-templates')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='pdf-templates' ? 'white' : '#072f53' }}>
                  <PictureAsPdfIcon />
                </ListItemIcon>
                <ListItemText primary="PDF-mallar" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {canAccessView('popup') && (
              <ListItemButton 
                selected={activeTab==='popup'} 
                onClick={(e)=>handleTabChange(e as any,'popup')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='popup' ? 'white' : '#072f53' }}>
                  <AnnouncementIcon />
                </ListItemIcon>
                <ListItemText primary="Popup-nyheter" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
            {canAccessView('imagemagick-test') && (
              <ListItemButton 
                selected={activeTab==='imagemagick-test'} 
                onClick={(e)=>handleTabChange(e as any,'imagemagick-test')}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#072f53',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0a3d6b' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab==='imagemagick-test' ? 'white' : '#072f53' }}>
                  <TerminalIcon />
                </ListItemIcon>
                <ListItemText primary="ImageMagick Test" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            )}
              </>
            )}

            {/* Media */}
            {showMediaSection && (
              <>
                <Divider sx={{ my: 2, mx: 2 }} />
                <ListSubheader sx={{ backgroundColor: 'transparent', fontWeight: 600, color: '#072f53', lineHeight: '32px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'static' }}>
                  Media
                </ListSubheader>
                {canAccessView('thumbnails') && (
                <ListItemButton 
                  selected={activeTab==='thumbnails'} 
                  onClick={(e)=>handleTabChange(e as any,'thumbnails')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='thumbnails' ? 'white' : '#072f53' }}>
                    <PhotoLibraryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Thumbnails" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
                {canAccessView('ai-queue') && (
                <ListItemButton 
                  selected={activeTab==='ai-queue'} 
                  onClick={(e)=>handleTabChange(e as any,'ai-queue')}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#072f53',
                      color: 'white',
                      '&:hover': { backgroundColor: '#0a3d6b' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: activeTab==='ai-queue' ? 'white' : '#072f53' }}>
                    <SmartToyIcon />
                  </ListItemIcon>
                  <ListItemText primary="AI-kö" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
                )}
              </>
            )}
          </List>
          
          {/* Version number */}
          {!settingsSidebarCollapsed && (
            <Box sx={{ p: 2, mt: 'auto', borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ color: '#999', display: 'block', textAlign: 'center' }}>
                Version {APP_VERSION}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Content area */}
        <Box sx={{ p: 2, flexGrow: 1, minWidth: 0, overflowY: 'auto', overflowX: 'auto' }}>
        {!firstAccessibleView && (
          <Alert severity="warning">
            Du har inga tillgängliga systeminställningar för din nuvarande roll.
          </Alert>
        )}
        {activeTab === 'thumbnails' && canAccessView('thumbnails') && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Thumbnails</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField type="number" label="Senaste timmar (sinceHours)" value={sinceHours} onChange={(e)=>setSinceHours(Number(e.target.value)||0)} size="small" sx={{ width: 220 }} />
              <TextField type="number" label="Max antal (limit)" value={limit} onChange={(e)=>setLimit(Number(e.target.value)||0)} size="small" sx={{ width: 180 }} />
              <FormControlLabel control={<Switch checked={scheduleEnabled} onChange={(e)=>setScheduleEnabled(e.target.checked)} />} label="Aktivera nattlig körning" />
              <TextField type="time" label="Tid (daglig)" value={scheduleTime} onChange={(e)=>setScheduleTime(e.target.value)} size="small" sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
              <Button variant="outlined" onClick={async ()=>{
                try {
                  await axiosInstance.post('/api/assets/previews/schedule', { enabled: scheduleEnabled, dailyTime: scheduleTime, sinceHours: sinceHours>0?sinceHours:24 });
                } catch {}
              }}>Spara schema</Button>
            </Box>
          </Paper>
        )}
        {warmResult && (
          <Alert severity={warmResult.failed ? 'warning' : 'success'} sx={{ mb: 2 }}>
            Värmning klar • skapade: {warmResult.generated}, hoppade över: {warmResult.skipped}, fel: {warmResult.failed}, totalt: {warmResult.totalTasks}
          </Alert>
        )}
        {activeTab === 'categories' && canAccessView('categories') && (
          <CategoryManagementPage 
            categories={categories}
            loading={loadingCategories}
            error={categoryError}
            onCategoryUpdated={fetchCategories}
          />
        )}
        {activeTab === 'badges' && canAccessView('badges') && (
          <BadgeManagementPage 
            categories={categories}
            loading={loadingBadges}
            error={badgeError}
            onBadgeUpdated={fetchCategoriesForBadges}
          />
        )}
        {activeTab === 'brand-guide' && canAccessView('brand-guide') && (
          <BrandGuideManagementPage
            initialSlug={brandGuideSlugToEdit}
            onInitialSlugHandled={onBrandGuideSlugHandled}
          />
        )}
        {activeTab === 'ppt-policies' && canAccessView('ppt-policies') && (
          <PptPoliciesManagementPage canPublish={hasPermission(currentUser, 'pptPolicies:publish')} />
        )}
        {activeTab === 'roles' && canAccessView('roles') && <RoleManagementPage />}
        {activeTab === 'users' && canAccessView('users') && <UserManagementPage />}
        {activeTab === 'apikeys' && canAccessView('apikeys') && <ApiKeyManagement />}
        {activeTab === 'backups' && canAccessView('backups') && <BackupManagement />}
        {activeTab === 'inventory' && canAccessView('inventory') && <ShopCart />}
        {activeTab === 'csv' && canAccessView('csv') && <CsvImportExport />}
        {activeTab === 'pptx-template-field-import' && canAccessView('pptx-template-field-import') && <PptxTemplateFieldImportPage />}
        {activeTab === 'field-diagnostics' && canAccessView('field-diagnostics') && <FieldDiagnostics />}
        {activeTab === 'favicon' && canAccessView('favicon') && <UploadFavicon />}
        {activeTab === 'support-page' && canAccessView('support-page') && <SupportPageSettings />}
        {activeTab === 'login-page' && canAccessView('login-page') && <LoginPageSettings />}
        {activeTab === 'ai-queue' && canAccessView('ai-queue') && <AiQueueManagement />}
        {activeTab === 'popup' && canAccessView('popup') && (
          <PopupSettingsModal 
            open={true} 
            onClose={() => {}} 
            embedded={true}
          />
        )}
        {activeTab === 'issues-tasks' && canAccessView('issues-tasks') && (
          <IssuesTasksPage currentUser={currentUser} />
        )}
        {activeTab === 'archive' && canAccessView('archive') && (
          <ArchivePage
            onClose={() => {}}
            canDownload={hasPermission(currentUser, 'assets:download')}
            canUpload={hasPermission(currentUser, 'assets:create')}
          />
        )}
        {activeTab === 'trash' && canAccessView('trash') && <TrashPage onClose={() => {}} />}
        {activeTab === 'duplicates' && canAccessView('duplicates') && <DuplicateListPage onClose={() => {}} />}
        {activeTab === 'download-templates' && canAccessView('download-templates') && <DownloadTemplatesPage />}
        {activeTab === 'analytics' && canAccessView('analytics') && <AnalyticsPage />}
        {activeTab === 'maintenance' && canAccessView('maintenance') && <MaintenanceSettingsPage />}
        {activeTab === 'imagemagick-test' && canAccessView('imagemagick-test') && <ImageMagickTestPage />}
        {activeTab === 'pdf-templates' && canAccessView('pdf-templates') && <PdfDesignerPage />}
        {activeTab === 'dabas-sync' && canAccessView('dabas-sync') && <DabasSyncPage />}
        {activeTab === 'product-comparison' && canAccessView('product-comparison') && <ProductComparisonPage />}
        {activeTab === 'product-image-validation' && canAccessView('product-image-validation') && <ProductImageValidationPage />}
        {activeTab === 'dev-snapshots' && canAccessView('dev-snapshots') && <DevSnapshotsPage />}
        {activeTab === 'knowledge-hub' && canAccessView('knowledge-hub') && <KnowledgeHubTestPage />}
        {activeTab === 'wordpress-publishing' && canAccessView('wordpress-publishing') && <WordPressPublishingPage />}
      </Box>
      </Box>
    {/* Progress dialog */}
    <Dialog open={warmDialogOpen} onClose={()=>{ if (!warming) setWarmDialogOpen(false); }}>
      <DialogTitle>Värmer thumbnails…</DialogTitle>
      <DialogContent>
        <Box sx={{ display:'flex', gap:2, py:1, minWidth: 360, flexDirection:'column', alignItems:'stretch' }}>
          <LinearProgress variant={warmProgress.totalTasks>0? 'determinate':'indeterminate'} value={warmProgress.totalTasks>0? Math.min(100, Math.round(((warmProgress.generated+warmProgress.skipped+warmProgress.failed)/Math.max(1,warmProgress.totalTasks))*100)) : undefined} />
          <Typography variant="body2">
            {warmProgress.totalTasks>0 ? `${warmProgress.generated+warmProgress.skipped+warmProgress.failed}/${warmProgress.totalTasks}` : 'Initierar…'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Skapade: {warmProgress.generated} • Hoppade över: {warmProgress.skipped} • Fel: {warmProgress.failed}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={async ()=>{
          try {
            const lines: string[] = [];
            lines.push(`Totalt: ${warmProgress.totalTasks}`);
            lines.push(`Skapade: ${warmProgress.generated}`);
            lines.push(`Hoppade över: ${warmProgress.skipped}`);
            lines.push(`Fel: ${warmProgress.failed}`);
            await navigator.clipboard.writeText(lines.join('\n'));
          } catch {}
        }} disabled={false}>Kopiera logg</Button>
        <Button onClick={()=> setWarmDialogOpen(false)} disabled={warming}>Dölj</Button>
      </DialogActions>
    </Dialog>
    </Box>
  );
};

export default SettingsPage; 